import { Loader2, Upload, User, AtSign, FileText, Link, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/components/AuthProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ProfileResponse } from '~/functions/api/get-profile/[[username]]';

import { debounce, formatURL, validateUsername } from '@/lib/utils';
import { RegistrationResponse } from '~/functions/api/register-user';
import { UsernameCheckResponse } from '@/types/api';
import { PLATFORMS } from '@/types/constants';

interface SocialLink {
    platform: string;
    url: string;
}


const EditUserPage = () => {
    const { user, setCurrentUser } = useAuth();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [usernameError, setUsernameError] = useState<string | null>(null);
    const [isUsernameAvailable, setIsUsernameAvailable] = useState(true);

    const [formData, setFormData] = useState({
        username: user?.username || '',
        displayName: user?.displayName || '',
        bio: user?.bio || '',
        userId: user?.id,
        email: user?.email,
    });

    const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(user?.imageUrl || null);

    // Check username availability
    const checkUsernameAvailability = async (username: string) => {
        if (username === user?.username) {
            setIsUsernameAvailable(true);
            setUsernameError(null);
            return;
        }

        try {
            setIsCheckingUsername(true);
            const response = await fetch(`/api/validate-username/${username}`);
            const data = await response.json() as UsernameCheckResponse;

            setIsUsernameAvailable(data.available);
            if (!data.available) {
                setUsernameError("Username not available");
            }
        } catch (error) {
            setUsernameError("Error checking username availability");
        } finally {
            setIsCheckingUsername(false);
        }
    };

    // Debounced username check
    const debouncedUsernameCheck = debounce(checkUsernameAvailability, 1000);


    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`/api/get-profile/${user?.username}`);
                const profileData = await response.json() as ProfileResponse;
                if (profileData) {
                    setFormData(prev => ({
                        ...prev,
                        username: profileData.username || user?.username || '',
                        displayName: profileData.display_name || user?.displayName || '',
                        bio: profileData.bio || user?.bio || '',
                        userId: user?.id,
                        email: user?.email,
                    }));

                    if (profileData.social_links && profileData.social_links.length > 0) {
                        setSocialLinks(profileData.social_links);
                    }

                    if (profileData.avatar_url) {
                        setImagePreview(profileData.avatar_url);
                    }
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
                setError('Failed to load profile data');
            }
        };

        if (user?.username) {
            fetchProfile();
        }
    }, [user?.username]);

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toLowerCase().trim();
        setFormData(prev => ({
            ...prev,
            username: value
        }));

        // First check basic validation
        const validationError = validateUsername(value);
        setUsernameError(validationError);

        // If basic validation passes, check availability
        if (!validationError && value !== user?.username) {
            debouncedUsernameCheck(value);
        } else if (value === user?.username) {
            setIsUsernameAvailable(true);
            setUsernameError(null);
        }
    };


    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddSocialLink = () => {
        setSocialLinks(prev => [...prev, { platform: PLATFORMS[0], url: '' }]);
    };

    const handleRemoveSocialLink = (index: number) => {
        setSocialLinks(prev => prev.filter((_, i) => i !== index));
    };

    const handleSocialLinkChange = (index: number, field: keyof SocialLink, value: string) => {
        setSocialLinks(prev => prev.map((link, i) => {
            if (i === index) {
                const newValue = field === 'url' ? formatURL(value) : value;
                return { ...link, [field]: newValue };
            }
            return link;
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user?.email || usernameError || isCheckingUsername || !formData.username) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('username', formData.username);
            formDataToSend.append('email', formData.email || '');
            formDataToSend.append('displayName', formData.displayName || '');
            formDataToSend.append('bio', formData.bio || '');
            formDataToSend.append('userId', formData.userId || '');

            if (imageFile) {
                formDataToSend.append('imageFile', imageFile);
            }

            // Add social links
            if (socialLinks.length > 0) {
                formDataToSend.append('socialLinks', JSON.stringify(socialLinks));
            }

            const response = await fetch('/api/edit-user', {
                method: 'PUT',
                body: formDataToSend,
            });

            if (!response.ok) {
                const data = await response.json() as RegistrationResponse;
                throw new Error(data.error || 'Failed to update profile');
            }
            const updatedUserData = await response.json() as RegistrationResponse;
            // Update the current user state with the response from the server
            setCurrentUser(updatedUserData.user || {
                id: user.id,
                email: user.email,
                username: user.username,
            })
            setSuccess(true);
            setTimeout(() => {
                navigate(`/${formData.username}`);
            }, 2000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Something went wrong');
        } finally {
            setIsLoading(false);
        }
    };

    // Username status message
    const getUsernameStatus = () => {
        if (isCheckingUsername) {
            return <p className="text-sm text-red-500"></p>;
        }
        if (usernameError) {
            return <p className="text-sm text-red-500">{usernameError}</p>;
        }
        if (formData.username !== user?.username && isUsernameAvailable && !usernameError) {
            return <p className="text-sm text-green-500">Username is available! ✨</p>;
        }
        return null;
    };
    return (
        <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
            <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">
                {/* Left side - Welcome message */}
                <div className="hidden md:block space-y-6 p-8">
                    <h1 className="text-4xl font-bold text-purple-900">Edit Your Profile</h1>
                    <p className="text-lg text-purple-700">Update your profile information</p>
                    <div className="space-y-4 text-purple-600">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <User className="w-4 h-4" />
                            </div>
                            <p>Update your profile picture</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <AtSign className="w-4 h-4" />
                            </div>
                            <p>Change your username</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <FileText className="w-4 h-4" />
                            </div>
                            <p>Edit your bio</p>
                        </div>
                    </div>
                </div>

                {/* Right side - Edit form */}
                <Card className="w-full shadow-lg border-0">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl">Edit Profile</CardTitle>
                        <CardDescription className="text-base">
                            Update your profile information
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex flex-col items-center space-y-4">
                                <div className="relative group">
                                    <Avatar className="w-32 h-32 border-4 border-purple-100 group-hover:border-purple-200 transition-all">
                                        <AvatarImage src={imagePreview || user?.imageUrl} />
                                        <AvatarFallback className="text-2xl bg-purple-100">
                                            {formData.displayName?.charAt(0) || formData.username?.charAt(0) || '?'}
                                        </AvatarFallback>
                                    </Avatar>
                                    <Input
                                        id="picture"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleImageChange}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => document.getElementById('picture')?.click()}
                                        className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 bg-white hover:bg-purple-50"
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        Change Photo
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="username" className="text-sm font-medium">Username</Label>
                                    <div className="relative">
                                        <Input
                                            id="username"
                                            value={formData.username}
                                            onChange={handleUsernameChange}
                                            required
                                            placeholder="Your username"
                                            className={`pl-8 ${usernameError ? "border-red-500 focus:ring-red-500" : "focus:ring-purple-500"}`}
                                        />
                                        <AtSign className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                    </div>
                                    {getUsernameStatus()}
                                    <p className="text-sm text-gray-500">
                                        Your profile: allmygigs.com/{formData.username}
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="displayName" className="text-sm font-medium">Display Name (Optional)</Label>
                                    <Input
                                        id="displayName"
                                        value={formData.displayName}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            displayName: e.target.value
                                        }))}
                                        placeholder="Enter this if you want to override your username"
                                        className="focus:ring-purple-500"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="bio" className="text-sm font-medium">Bio</Label>
                                    <textarea
                                        id="bio"
                                        value={formData.bio}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            bio: e.target.value
                                        }))}
                                        placeholder="Tell us about yourself..."
                                        className="w-full h-24 px-3 py-2 border rounded-md focus:ring-purple-500 focus:border-purple-500"
                                    />
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm font-medium">Social Links</Label>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAddSocialLink}
                                            className="text-purple-600 hover:text-purple-700"
                                            disabled={socialLinks.length >= PLATFORMS.length}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Add Link
                                        </Button>
                                    </div>

                                    {socialLinks.map((link, index) => (
                                        <div key={index} className="flex gap-2 items-start">
                                            <select
                                                value={link.platform}
                                                onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                                                className="w-1/3 px-3 py-2 border rounded-md focus:ring-purple-500 focus:border-purple-500"
                                            >
                                                {PLATFORMS.map(platform => (
                                                    <option key={platform} value={platform}>
                                                        {platform}
                                                    </option>
                                                ))}
                                            </select>
                                            <Input
                                                value={link.url}
                                                onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                                                placeholder={`Your ${link.platform} URL`}
                                                className="flex-1"
                                                type="url"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemoveSocialLink(index)}
                                                className="text-gray-400 hover:text-red-500"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {error && (
                                <Alert variant="destructive" className="mt-4">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            {success && (
                                <Alert className="mt-4 bg-green-50 text-green-700 border-green-200">
                                    <AlertDescription>
                                        Profile updated successfully! Redirecting... ✨
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => navigate(`/${user?.username}`)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                                    disabled={isLoading || !formData.username || !!usernameError || isCheckingUsername || !isUsernameAvailable || success}
                                >
                                    {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Save Changes
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );;
};

export default EditUserPage;